import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useHashAnchor } from '../../hooks/useHashAnchor';
import Footer from '../../components/Footer';
import AppStoreBadges from '../../components/AppStoreBadges';
import FeatureIcon, { ICON_TONES, IconTone } from '../../components/FeatureIcon';
import { TIER_COLORS } from '../../components/canonicalAccents';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import MarketingScreenshot from '../../components/MarketingScreenshot';
import { getScreenshotPreview, getScreenshotPreviewSet } from '../../lib/screenshotPreview';
import { CAPTURE_BASES, selectedCaptureSrc } from '../../lib/selectedCaptures';
import CommunityReputation from './CommunityReputation';
import './SocialFan.css';
const photoHeading = '#f8fafc';
const photoBody = 'rgba(226,232,240,0.9)';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const Social: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();
  useHashAnchor();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const deviceBorder = theme === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)';
  const deviceFrame = 'rounded-[1.6rem] border-4 overflow-hidden shadow-2xl';

  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg sm:text-xl max-w-2xl leading-relaxed';

  const friendsFeatures = [
    { title: 'Send & Accept Friend Requests', description: 'Build your riding network by connecting with riders you meet at events' },
    { title: 'Mutual Friends', description: 'See who you have in common with other riders to build trust and expand your network' },
    { title: 'Connect at Events', description: 'Send a request from any rider\'s profile after a ride together' },
    { title: 'Privacy Controls', description: 'Control what friends can see - from full access to minimal information' }
  ];

  const crewSteps = [
    { title: 'Earned Reputation', sub: 'Build your riding reputation through feedback from the people you ride with.' },
    { title: 'Keep the Riders You Click With', sub: "Good rides turn into regular crews. Send a friend request from any rider's profile, and your circle grows with every event." },
    { title: 'Your Riding Story', sub: 'Keep your riding history and milestones together as your journey grows.' }
  ];

  const profileFeatures = [
    { title: 'Display Name & Bio', description: 'Share your riding story and personality' },
    { title: 'Location Display', description: 'Show your general area to find local riders' },
    { title: 'Public Garage', description: 'Showcase your motorcycles with photos and details' },
    { title: 'Event Statistics', description: 'Display your total events, miles ridden, and events hosted' },
    { title: 'Skill Level', description: 'Show your riding experience and comfort level' },
    { title: 'Favorite Ride Type', description: 'Display your preferred event types (Cruise, Tour, etc.)' }
  ];

  const privacyPresets = [
    { level: 'Stay Low-Key', desc: 'Share the minimum and ride with peace of mind' },
    { level: 'Make Connections', desc: 'Share enough to make friends, keep the rest private' },
    { level: 'Get Noticed', desc: 'Maximize your visibility and build your reputation' }
  ];

  const achievementLevels = [
    { level: 'Bronze', points: '10-25 points', example: 'First ride, getting started', color: TIER_COLORS.bronze },
    { level: 'Silver', points: '50-75 points', example: '10 rides, building habits', color: TIER_COLORS.silver },
    { level: 'Gold', points: '100-200 points', example: '25 rides, dedicated rider', color: TIER_COLORS.gold },
    { level: 'Platinum', points: '250+ points', example: '50+ rides, elite status', color: TIER_COLORS.platinum }
  ];

  const achievementCategories = [
    { category: 'Participation', description: 'Earn badges for attending group rides', examples: ['Kickstands Up', 'Pavement Pounder'] },
    { category: 'Event Hosting', description: 'Recognition for organizing and leading rides', examples: ['Pack Leader', 'Road Captain'] },
    { category: 'Dedication', description: 'Time-based participation achievements', examples: ['Dawn Patrol', 'Moonlighter'] },
    { category: 'Community', description: 'Building connections and engagement', examples: ['Crew Chief'] },
    { category: 'Special', description: 'Unique accomplishments and variety', examples: ['All Roads Lead'] }
  ];

  const achievementHowItWorks = [
    { title: 'Automatic Tracking', description: 'Achievements are evaluated when each event wraps up. Progress tracked automatically.' },
    { title: 'Instant Rewards', description: 'Badges land on your profile the moment requirements are met.' },
    { title: 'Track Progress', description: 'View progress bars on locked achievements. See how close you are to your next milestone.' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* 1 · Hero — mountain overlook; copy in the open valley/sky on the left */}
      <section className="relative bg-[#0a0d14] md:bg-transparent">
        <div className="md:hidden aspect-[16/10] bg-cover bg-[position:65%_center]" style={{ backgroundImage: `url('/assets/images/ridepulse-community-mountain-overlook-v1.png')` }} role="img" aria-label="Three riders talking beside their motorcycles at a mountain overlook" />
        <div
          className="absolute inset-0 hidden md:block bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(100deg, rgba(8,10,16,0.68) 0%, rgba(8,10,16,0.48) 42%, rgba(8,10,16,0.12) 68%, transparent 85%), url('/assets/images/ridepulse-community-mountain-overlook-v1.png')`
          }}
        />
        <div className="relative container mx-auto px-4 sm:px-6 max-w-6xl py-16 md:py-32 lg:py-40">
          <div className="max-w-2xl text-center md:text-left mx-auto md:mx-0">
            <p className={eyebrow} style={{ color: dsColors.primary }}>Community &amp; Social</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: photoHeading, textWrap: 'balance' }}>
              Build Your Riding<br className="hidden md:block" /> Community
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 leading-relaxed" style={{ color: photoBody, textWrap: 'pretty' }}>
              Connect with riders, showcase your profile, and earn achievements as you build your riding journey.
            </p>
            <div className="flex justify-center md:justify-start gap-4 flex-wrap">
              <AppStoreBadges size="large" showApple showAndroid />
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Friends & Connections */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Friends &amp; Connections</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Build your riding network and connect with motorcycle enthusiasts.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-14 items-center mb-14">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto lg:mx-0 w-full text-left">
                {friendsFeatures.map((f, i) => (
                  <li key={f.title} className="flex gap-4 p-5 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                    <span
                      className="flex-shrink-0 inline-flex w-10 h-10 rounded-xl items-center justify-center"
                      style={{
                        backgroundColor: `${dsColors[ICON_TONES[(['community', 'community', 'community', 'safety'] as IconTone[])[i]]]}1f`,
                        color: dsColors[ICON_TONES[(['community', 'community', 'community', 'safety'] as IconTone[])[i]]],
                      }}
                      aria-hidden="true"
                    >
                      <FeatureIcon name={(['users', 'link', 'calendar', 'shield'] as const)[i]} />
                    </span>
                    <div>
                      <h3 className="font-semibold text-base mb-1" style={{ color: heroPrimary }}>{f.title}</h3>
                      <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{f.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <MarketingScreenshot
                base={CAPTURE_BASES.friends}
                alt="The friends list in RidePulse showing CanyonRunner and SunnyCruiser with photos and GearHead with their handles"
                caption="Your connections."
              />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-8" style={{ color: heroPrimary, textWrap: 'balance' }}>
              From Ride to Crew
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {crewSteps.map((s) => (
                <div key={s.title}>
                  <h4 className="text-sm font-bold mb-2" style={{ color: heroPrimary }}>{s.title}</h4>
                  <p className="text-xs leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>{s.sub}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 3 · Public Profiles — tilted sample-data trio + feature ticks */}
      <section id="public-profiles" className="py-16 md:py-24 scroll-mt-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Public Profiles</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Your riding story, with a choice about what you share.
              </p>
            </div>
            {/* Profile, statistics and achievements supporting devices. */}
            <div className="social-fan mx-auto mb-4">
              {([
                {
                  cls: 'social-fan-public',
                  src: selectedCaptureSrc('publicProfile', theme),
                  alt: 'RidePulse public rider profile showing bio, motorcycle and riding details',
                  caption: 'Public profile.',
                },
                {
                  cls: 'social-fan-stats',
                  src: selectedCaptureSrc('publicStats', theme),
                  alt: 'RidePulse public rider statistics showing events hosted and riders',
                  caption: 'Riding statistics.',
                },
                {
                  cls: 'social-fan-achievements',
                  src: selectedCaptureSrc('achievements', theme),
                  alt: 'RidePulse rider achievements with XP progress and badges',
                  caption: 'Achievements.',
                },
              ]).map((s) => (
                <figure key={s.cls} className={`social-fan-item ${s.cls} m-0`}>
                  <div
                    className={`${deviceFrame} block rounded-[1.6rem]`}
                    style={{ borderColor: deviceBorder }}
                  >
                    <img src={getScreenshotPreview(s.src)} srcSet={getScreenshotPreviewSet(s.src)} sizes="(min-width: 1024px) 320px, 60vw" alt={s.alt} className="w-full block" width={1320} height={2868} loading="lazy" />
                  </div>
                  <figcaption className="text-xs mt-3 text-center" style={{ color: heroSecondary }}>{s.caption}</figcaption>
                </figure>
              ))}
            </div>
            <div className="max-w-4xl mx-auto rounded-2xl p-6 sm:p-8" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-6 text-center" style={{ color: dsColors.treadGray }}>
                What shows on your profile
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
                {profileFeatures.map((f) => (
                  <div key={f.title}>
                    <dt className="font-semibold text-sm mb-0.5" style={{ color: heroPrimary }}>{f.title}</dt>
                    <dd className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{f.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 4 · Reputation — feedback from shared rides; host/rider split + privacy note */}
      <CommunityReputation />

      {/* 5 · Profile & Safety — privacy levels */}
      <section id="profile-privacy" className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Profile &amp; Safety</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Customize how you appear to others. Block or report users who don't belong.
              </p>
            </div>
            <div className="text-center mb-8">
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-4 text-xs font-bold"
                style={{ backgroundColor: `${dsColors[ICON_TONES.safety]}1f`, color: dsColors[ICON_TONES.safety] }}
              >
                <FeatureIcon name="shield" size={13} /> PRIVACY FIRST
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: heroPrimary, textWrap: 'balance' }}>
                You Control Who Sees What
              </h3>
              <p className="text-sm max-w-xl mx-auto" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Every piece of your profile is under your control. Choose a preset that fits how you ride.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {privacyPresets.map((item) => (
                <div
                  key={item.level}
                  className="p-5 rounded-xl text-center"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}
                >
                  <h4 className="text-sm font-bold mb-2" style={{ color: dsColors[ICON_TONES.safety] }}>{item.level}</h4>
                  <p className="text-xs leading-relaxed" style={{ color: heroSecondary }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 6 · Achievements & Badges — tier strip + category cards */}
      <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Achievements &amp; Badges</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Earn recognition for your riding milestones and community contributions.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {achievementLevels.map((item) => (
                <div
                  key={item.level}
                  className="p-5 rounded-xl text-center"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}
                >
                  <h4 className="text-lg font-bold mb-1" style={{ color: item.color }}>{item.level}</h4>
                  <p className="text-sm font-semibold mb-2" style={{ color: heroSecondary }}>{item.points}</p>
                  <p className="text-xs" style={{ color: heroSecondary }}>{item.example}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {achievementCategories.map((cat) => (
                <div
                  key={cat.category}
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}
                >
                  <h3 className="text-sm font-bold mb-1" style={{ color: heroPrimary }}>{cat.category}</h3>
                  <p className="text-xs mb-3" style={{ color: heroSecondary, textWrap: 'pretty' }}>{cat.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.examples.map((example) => (
                      <span
                        key={example}
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{ backgroundColor: `${dsColors.treadGray}1f`, color: heroPrimary }}
                      >
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 7 · How Achievements Work — quiet close */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>How Achievements Work</h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto mb-10 text-left">
              {achievementHowItWorks.map((f) => (
                <li key={f.title} className="p-5 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}`, borderTop: `3px solid ${dsColors[ICON_TONES.attention]}` }}>
                  <h3 className="font-semibold text-base mb-1" style={{ color: heroPrimary }}>{f.title}</h3>
                  <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{f.description}</p>
                </li>
              ))}
            </ul>
            <p className="text-sm font-semibold text-center mb-4" style={{ color: heroSecondary }}>
              Must check in to events (not just RSVP) • Event must be completed • No manual claiming needed
            </p>
            <p className="text-center">
              <Link
                to="/help#achievements"
                className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                See how achievements work →
              </Link>
            </p>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Social;
